/**
 * Helper functions to support device environment
 * like detecting the device size and reducing settings
 *
 * @packageDocumentation
 */

/**
 * Media query's breakpoints
 * 320, 420, 768, 1024, 1200
 * See $breakpoints map in scss variables
 *
 * @enum {string}
 */
enum MediaQueries {
    maxSmallBP = '(max-width: 767px)',
    maxMediumBP = '(max-width: 1023px)',
    touchMotionSensor = '(pointer: coarse)',
    mouseStylus = '(pointer: fine)',
    hover = '(hover: hover)',
    reduceMotion = '(prefers-reduced-motion: reduce)',
    reduceData = '(prefers-reduced-data: reduce)'
}

/**
 * determine if the Document matches the media query
 * method to determine if "mobile" max 767 breakpoint
 *
 * @returns {boolean}
 */
export const isMobile = (): boolean => {
    return window.matchMedia(MediaQueries.maxSmallBP).matches
}

/**
 * determine if the Document matches the media query
 * method to determine if "tablet" max 1023 breakpoint
 * and not matched to "mobile"
 *
 * @returns {boolean}
 */
export const isTablet = (): boolean => {
    return window.matchMedia(MediaQueries.maxMediumBP).matches && !isMobile()
}

/**
 * determine if device/browser matches the media query
 * method for having ability to touch
 *
 * @returns {boolean}
 */
export const hasTouch = (): boolean => {
    const navigatorTouch =
        (navigator && navigator.maxTouchPoints > 0) ||
        (navigator && navigator.msMaxTouchPoints > 0)

    return (
        window.matchMedia(MediaQueries.touchMotionSensor).matches ||
        'ontouchstart' in window ||
        navigatorTouch
    )
}

/**
 * @return MediaQueryList (properties, methods, events)
 * https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList
 */
export const reduceMotionQueryList = (): MediaQueryList => {
    return window.matchMedia(MediaQueries.reduceMotion)
}

/**
 * determine if device/browser matches the media query
 * method for user preferring less motion
 *
 * @returns {boolean}
 */
export const isReduceMotion = (): boolean => {
    return window.matchMedia(MediaQueries.reduceMotion).matches
}
